/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { StringParam, useQueryParam } from 'use-query-params';
import { usePostRequest } from '../../../../../../../api/helper';
import VAR_COMMON_CONSTANTS from "../../../../../../../constants/globalVariables";
import { selectCurrentUser } from '../../../../../../../redux/ducks/AccessControl/slice';
import { useAppSelector } from '../../../../../../../redux/hooks';
import ArgosButton from '../../../../../../components/Buttons/ArgosButton';
import ArgosFlexbox from '../../../../../../components/Containers/ArgosFlexbox';
import ArgosDialogHeader from '../../../../../../components/Dialogs/ArgosDialogHeader';
import EditMergeSetDetails from '../../../../../../components/Dialogs/RedlineAppDialogs/EditMergeSetDetails';
import { mergeDatasetsCreateEndpoint } from '../../../../constants';
import { COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM, MERGE_MODES } from '../../../constants';
import { Container, Header, IDialogProps } from './constants';

interface IMergeSettings {
  Capability: boolean
  Modality: boolean
  DataFlow: boolean
  Safety: boolean
  noStrategy: boolean
}

const defaultMergeSettings: IMergeSettings = {
  Capability: true,
  Modality: true,
  DataFlow: true,
  Safety: false,
  noStrategy: false
}

const MergeDatasetsDialog = (props: IDialogProps) => {
  const {studyName} = props
  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const fetchMergeDatasetsHandle = usePostRequest(mergeDatasetsCreateEndpoint)
  const [mergeSetData, setMergeSetData] = useState<any>({
    "name":"",
    "description": "",
    "owner": gmid,
    "mergeSettings": {
      Capability: true,
      Modality: true,
      DataFlow: true,
      Safety: false,
      noStrategy: false
    }
  })

  const [leftSidebarTabIndex, setLeftSidebarTabIndex] = useQueryParam(COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM, StringParam)
  const onClickMergeDatasets = () => {
    props.setIsMerging(true)
    props?.onCloseDialog()

    //pass any existing url params, will update datasetid and datasetname on the backend
    fetchMergeDatasetsHandle({
      "mode": MERGE_MODES.CREATE,
      "studyId": props.studyId,
      "studyName": studyName,
      "creator": gmid,
      "owner": mergeSetData.owner,
      "generator": gmid,
      "mergedDatasetName": mergeSetData.name,
      "description": mergeSetData.description, 
      "mergeSettings": mergeSetData.mergeSettings, 
      "datasetIds": props.selectedDatasets,
    }).catch((e) => {
      console.log(e)
      toast.error("An error occurred while creating merged data sets")
    }).finally(() => {
      setLeftSidebarTabIndex("Merged Data Sets")
      props.setIsMerging(false)
    })
  }


  const handleMergeSetDataChange = (data: any) => {
    setMergeSetData(data)
  }


  return (
    <Container>
      <ArgosDialogHeader>
        <ArgosFlexbox style={{justifyContent: 'space-between', alignItems: 'center'}}>
          <Header>
            <div style={{
              color: 'white',
              paddingLeft: 5,
              fontSize: 14
            }}>{"Merge "+VAR_COMMON_CONSTANTS.VAR_DATA_SETS}</div>
          </Header>
          <IconButton onClick={props?.onClickCancel} sx={{color: 'white', padding: 0, fontSize: 'small'}}> 
            <CloseIcon />
          </IconButton>
        </ArgosFlexbox>
      </ArgosDialogHeader>
      <EditMergeSetDetails action={MERGE_MODES.CREATE} mergeSetData={mergeSetData} userData={props.userData} onChange={handleMergeSetDataChange}/>
      <div style={{alignSelf: 'center', display: 'flex', gap: 10, marginTop: 10}}>
        <ArgosButton 
          onClick={props?.onClickCancel}
        >
          Cancel
        </ArgosButton>
        <ArgosButton 
          onClick={onClickMergeDatasets} 
          disabled={mergeSetData.name === "" || mergeSetData.description === "" || mergeSetData.owner === ""} 
        >
          Merge
        </ArgosButton>
      </div>
    </Container>
  )
}

export default MergeDatasetsDialog
